import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import Pagination from '@/components/pagination';
import { SortField, SortState } from './student-meetings.types';
import './student-meetings.scss';
import { useApi } from '@/api/useApi.tsx';
import { useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { AvailableHourResponseLiveServiceGroupEnum, InteractionResponse } from '@dopingtech/live_expert_bff_api_ts/api.ts';
import Modal from '@/components/modal/Modal.tsx';
import { GOT_IT_MEETING_TYPE, INSTANT_MEETING_TYPE, INSTANT_MEETING_TYPE_TEXT, LIVE_SERVICE_TYPES, MEETING_TYPES, MEETING_SERVICE_TYPES } from '@/constants';
import LoadingSpinner from '@/components/loading-spinner';
import { isEmptyObject } from '@/utility';
import { Button } from '@/components';
import { useSizes } from '@/utility/responsive.ts';

const ITEMS_PER_PAGE = 5;

type MeetingCommentDataType = {
	title: string | null;
	message: string | null;
};

interface StudentMeetingsProps {
	studentId: string;
	liveServiceGroup?: AvailableHourResponseLiveServiceGroupEnum;
}

const StudentMeetings: React.FC<StudentMeetingsProps> = ({ studentId, liveServiceGroup = null }) => {
	const [currentPage, setCurrentPage] = useState(1);
	const [sortState, setSortState] = useState<SortState>({
		field: null,
		direction: 'asc'
	});
	const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
	const [selectedDate, setSelectedDate] = useState(dayjs());
	const { InteractionControllerApi } = useApi();
	const { isDesktop } = useSizes();

	const meetingCommentRef = useRef<MeetingCommentDataType>({
		title: null,
		message: null
	});
	const [meetingCommentIsLoading, setMeetingCommentIsLoading] = useState(false);
	const [showMeetingCommentModal, setShowMeetingCommentModal] = useState(false);

	const { data: pastMeetingData } = useQueryProvider([QUERY_KEYS.findInteractionsByStudentId, studentId, liveServiceGroup, currentPage], () => InteractionControllerApi.findInteractionsByStudentId(parseInt(studentId), liveServiceGroup as typeof LIVE_SERVICE_TYPES[keyof typeof LIVE_SERVICE_TYPES], currentPage - 1, ITEMS_PER_PAGE), {
		select: ({ data }) => data.data,
		refetchOnWindowFocus: true,
		enabled: !!studentId
	});

	const datePickerRef = useRef<HTMLDivElement | null>(null);

	const handleSort = (field: SortField) => {
		setSortState(prevState => ({
			field,
			direction: prevState.field === field && prevState.direction === 'asc' ? 'desc' : 'asc'
		}));
	};

	const getSortedMeetings = (meetings: InteractionResponse[] | undefined) => {
		if (!meetings || !sortState.field) return meetings || [];

		return [...meetings].sort((a, b) => {
			if (sortState.field === 'date') {
				return sortState.direction === 'asc'
					? dayjs(a.interactionStartDate).valueOf() - dayjs(b.interactionStartDate).valueOf()
					: dayjs(b.interactionStartDate).valueOf() - dayjs(a.interactionStartDate).valueOf();
			}

			if (sortState.field === 'rating') {
				const ratingA = a.rating || 0;
				const ratingB = b.rating || 0;
				return sortState.direction === 'asc' ? ratingA - ratingB : ratingB - ratingA;
			}

			return 0;
		});
	};

	const paginatedMeetings = useMemo(() => {
		return getSortedMeetings(pastMeetingData?.content);
	}, [pastMeetingData?.content, sortState]);

	const totalPages = pastMeetingData?.totalPages || 0;

	const getRatingColor = useCallback((rating: number): string => {
		if (rating >= 4) return '#FFD012';
		if (rating === 3) return '#E67E22';
		return 'var(--danger-text)';
	}, []);

	const renderRatingStars = useCallback(
		(rating: number) => {
			const ratingColor = getRatingColor(rating);
			return [...Array(5)]?.map((_, index) => (
				<i
					key={index}
					className={`icons-star ${index < rating ? '' : 'inactive'}`}
					style={index < rating ? { color: ratingColor } : undefined}
				/>
			));
		},
		[getRatingColor]
	);

	const getSortIcon = (field: SortField) => {
		if (sortState.field === field) {
			return <i className={`icons-alt-arrow-${sortState.direction === 'asc' ? 'down' : 'up'}`} />;
		}
		return <i className="icons-alt-arrow-down" />;
	};

	const getServiceType = useCallback((liveServiceGroup?: string, meetingType?: string) => {
		if (liveServiceGroup === LIVE_SERVICE_TYPES.COACHING && meetingType === MEETING_TYPES.INSTANT) {
			return MEETING_SERVICE_TYPES.GUIDANCE;
		}
		if (liveServiceGroup === LIVE_SERVICE_TYPES.COACHING && meetingType === MEETING_TYPES.APPOINTMENT) {
			return MEETING_SERVICE_TYPES.ONE_ON_ONE_COACHING;
		}
		if (liveServiceGroup === LIVE_SERVICE_TYPES.GOT_IT && meetingType === MEETING_TYPES.INSTANT) {
			return MEETING_SERVICE_TYPES.LECTURE;
		}
		if (liveServiceGroup === LIVE_SERVICE_TYPES.GOT_IT && meetingType === MEETING_TYPES.APPOINTMENT) {
			return MEETING_SERVICE_TYPES.ONE_ON_ONE_LECTURE;
		}

		return '';
	}, []);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent): void => {
			if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
				setIsDatePickerOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const monthOptions = useMemo(() => {
		const options = [];
		const currentDate = dayjs();

		for (let i = 0; i < 12; i++) {
			const date = currentDate.subtract(i, 'month');
			options.push({
				value: date.format('YYYY-MM'),
				label: date.locale('tr').format('MMMM YYYY')
			});
		}
		return options;
	}, []);

	const handleMonthSelect = (date: string) => {
		setSelectedDate(dayjs(date));
		setIsDatePickerOpen(false);
	};

	const handleShowComment = async (type: string, meeting: InteractionResponse) => {
		try {
			setMeetingCommentIsLoading(true);
			const { data } = await InteractionControllerApi.findInteractionCommentsById(meeting?.id || 0);
			if (!isEmptyObject(data?.data)) {
				if (liveServiceGroup === AvailableHourResponseLiveServiceGroupEnum.GotIt) {
					meetingCommentRef.current = {
						title: GOT_IT_MEETING_TYPE[type as keyof typeof GOT_IT_MEETING_TYPE],
						message: data?.data?.[type as keyof typeof data.data] || null
					};
				} else {
					meetingCommentRef.current = {
						title: INSTANT_MEETING_TYPE_TEXT[type],
						message: data?.data?.[type as keyof typeof data.data] || null
					};
				}
			} else {
				meetingCommentRef.current = {
					title: 'Kayıt Bulunamadı',
					message: `${INSTANT_MEETING_TYPE_TEXT[type]} için herhangi bir kayıt bulunamadı.`
				};
			}

			setShowMeetingCommentModal(true);
			setMeetingCommentIsLoading(false);
		} catch (error) {
			console.error('Error retrieving comment data:', error);
			setMeetingCommentIsLoading(false);
		}
	};

	const renderPastMeetingTable = () => {
		if (isDesktop) {
			return (
				<>
					{paginatedMeetings?.map((meeting, index) => (
						<div
							key={meeting.id}
							className="student-meetings__meeting-row">
							<div className="student-meetings__col-id">{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</div>

							<div className="student-meetings__col-student">
								<img
									src={meeting?.expertAvatarUrl}
									alt={meeting?.expertName}
								/>
								<div className="student-meetings__student-info">
									<div className="student-meetings__name">{`${meeting?.expertName} ${meeting?.expertSurname}.`}</div>
									<div className="student-meetings__grade">{meeting?.studentGradeName}</div>
								</div>
							</div>

							<div className="student-meetings__col-subject">
								<span>{meeting?.meetingSubjectName}</span>
							</div>

							<div className="student-meetings__col-service-type">
								<span>{getServiceType(meeting?.liveServiceGroup, meeting?.meetingType)}</span>
							</div>

							<div className="student-meetings__col-rating">
								{meeting.rating ? (
									<div className="student-meetings__stars">{renderRatingStars(meeting.rating)}</div>
								) : (
									<span className="student-meetings__rating-badge">Henüz puanlanmadı</span>
								)}
							</div>

							<div className="student-meetings__col-evaluation">
								{liveServiceGroup === AvailableHourResponseLiveServiceGroupEnum.GotIt ? (
									<button onClick={() => handleShowComment(GOT_IT_MEETING_TYPE.COMMENT_TO_OPERATION, meeting)}>
										<i className="icons-users-group-rounded"></i>
									</button>
								) : (
									<>
										<button onClick={() => handleShowComment(INSTANT_MEETING_TYPE.COMMENT_TO_STUDENT, meeting)}>
											<i className="icons-chat-line"></i>
										</button>
										<button onClick={() => handleShowComment(INSTANT_MEETING_TYPE.COMMENT_TO_PARENT, meeting)}>
											<i className="icons-users-group-rounded"></i>
										</button>
										<button onClick={() => handleShowComment(INSTANT_MEETING_TYPE.COMMENT_TO_BACKOFFICE, meeting)}>
											<i className="icons-checklist-minimalistic"></i>
										</button>
									</>
								)}
							</div>
						</div>
					))}
				</>
			);
		} else {
			return (
				<div>
					{paginatedMeetings?.map(meeting => (
						<div
							key={meeting.id}
							className={`student-meetings__mobile-meeting-row ${meeting?.meetingType}`}>
							<div className={'student-meetings__col-header'}>
								<div className="student-meetings__col-header-student">
									<img
										src={meeting?.studentAvatarUrl}
										alt={meeting?.studentName}
									/>
									<div className="student-meetings__student-info">
										<div className="student-meetings__name">{`${meeting?.studentName} ${meeting?.studentSurname}`}</div>
										<div className="student-meetings__col-header-information-date">
											<span>{dayjs(meeting.interactionStartDate).format('D MMMM, HH:mm')}</span>
										</div>
									</div>
								</div>
								<div className={'student-meetings__col-header-information'}>
									<div className="student-meetings__col-header-information-rating">
										{meeting.rating && <div className="student-meetings__stars">{renderRatingStars(meeting.rating)}</div>}
									</div>
									<div>
										<Button
											size={'small'}
											variant={'secondary'}
											className={'student-meetings__system-report-button'}
											onClick={() => handleShowComment(INSTANT_MEETING_TYPE.COMMENT_TO_BACKOFFICE, meeting)}>
											<i className="icons-checklist-minimalistic" />
										</Button>
									</div>
								</div>
							</div>
							<div className={'student-meetings__col-body'}>
								<div className={'student-meetings__col-body-subject'}>
									<p>
										<b>Konu: </b>
										<span>{meeting?.meetingSubjectName}</span>
									</p>
								</div>

								<div className={'student-meetings__col-body-subject'}>
									<p>
										<b>Hizmet Tipi: </b>
										<span>{getServiceType(meeting?.liveServiceGroup, meeting?.meetingType)}</span>
									</p>
								</div>
							</div>

							<div className={'student-meetings__col-footer'}>
								<div className={'student-meetings__col-show-report'}>
									<Button
										fullWidth={true}
										size={'small'}
										variant={'secondary'}
										className={'student-meetings__col-show-report-button'}
										onClick={() => handleShowComment(INSTANT_MEETING_TYPE.COMMENT_TO_PARENT, meeting)}>
										<i className="icons-users-group-rounded" />
										Veli Özeti
									</Button>

									<Button
										fullWidth={true}
										size={'small'}
										variant={'secondary'}
										className={'student-meetings__col-show-report-button'}
										onClick={() => handleShowComment(INSTANT_MEETING_TYPE.COMMENT_TO_STUDENT, meeting)}>
										<i className="icons-chat-line" />
										Öğrenci Özeti
									</Button>
								</div>
							</div>
						</div>
					))}
				</div>
			);
		}
	};

	if (!pastMeetingData?.content?.length) {
		return (
			<div className="student-meetings">
				<div className="student-meetings__empty-state">
					<p>Bu öğrenci için henüz görüşme kaydı bulunmamaktadır.</p>
				</div>
			</div>
		);
	}

	return (
		<div className="student-meetings">
			<div className="student-meetings__page-header">
				<h1>Görüşme Geçmişi</h1>
				<div
					className="student-meetings__date-picker"
					ref={datePickerRef}>
					{isDatePickerOpen && (
						<div className="student-meetings__month-dropdown">
							{monthOptions?.map(month => (
								<div
									key={month.value}
									className={`student-meetings__month-option ${month.value === selectedDate.format('YYYY-MM') ? 'selected' : ''}`}
									onClick={() => handleMonthSelect(month.value)}>
									{month.label}
								</div>
							))}
						</div>
					)}
				</div>
			</div>

			<div className="student-meetings__meetings-table">
				<div className="student-meetings__table-header desktop">
					<div className="student-meetings__col">No</div>
					<div
						className="student-meetings__col sortable"
						onClick={() => handleSort('rating')}>
						<span>Koç</span>
						{getSortIcon('coach')}
					</div>

					<div
						className="student-meetings__col sortable"
						onClick={() => handleSort('rating')}>
						<span>Görüşme Konusu</span>
						{getSortIcon('subject')}
					</div>

					<div className="student-meetings__col">
						<span>Hizmet Tipi</span>
					</div>

					<div
						className="student-meetings__col sortable"
						onClick={() => handleSort('rating')}>
						<span>Puan</span>
						{getSortIcon('rating')}
					</div>

					<div className="student-meetings__col">Değerlendirmeler</div>
				</div>

				<div className="student-meetings__table-body">{renderPastMeetingTable()}</div>
			</div>

			{totalPages > 1 && (
				<div className="student-meetings__pagination-container">
					<Pagination
						currentPage={currentPage}
						totalPages={totalPages}
						onPageChange={setCurrentPage}
					/>
				</div>
			)}

			<Modal
				size={'md'}
				isOpen={showMeetingCommentModal}
				showCloseButton
				variant="centered"
				className={'student-meetings__past-meeting-comment-modal-container'}
				bodyClassName={'student-meetings__past-meeting-comment-modal'}
				onClose={() => setShowMeetingCommentModal(false)}>
				{meetingCommentIsLoading ? (
					<LoadingSpinner loadingText={'Yükleniyor'} />
				) : (
					<>
						<h3 className="student-meetings__past-meeting-comment-modal-title">{meetingCommentRef?.current?.title}</h3>
						<div className="student-meetings__past-meeting-comment-modal-message">{meetingCommentRef?.current?.message}</div>
					</>
				)}
			</Modal>
		</div>
	);
};

export default StudentMeetings;
